// Agrega o Atendimento Humano (Handoff/Operador) ao nível do grupo para o Dashboard —
// módulo licenciado por hotel (`GET /human-handoff/admin/analytics?hotel_id=`, gated por
// require_handoff_manage), tal como CSAT e Ofertas Especiais (ver csatMetrics.js/
// offersMetrics.js) — por isso agregamos aqui, no cliente, por hotel do grupo.
//
// `/analytics` já devolve total/by_state/avg_rating/avg_wait/avg_handle agregados no servidor
// (sem teto), mas não motivos de encerramento nem distribuição horária — para isso usamos
// `/human-handoff/admin/sessions` (capado a 500 por hotel, mesma classe de limitação documentada
// em dashboardMetrics.js para `messages`) e agregamos motivos/horas no cliente.

export const CLOSE_REASON_LABELS = {
  closed: 'Fechado pelo operador',
  ended_by_visitor: 'Terminado pelo visitante',
  timeout: 'Tempo esgotado (sem resposta)',
  unavailable: 'Sem operadores disponíveis',
  module_disabled: 'Módulo desativado',
};

function reasonLabel(reason) {
  return CLOSE_REASON_LABELS[reason] || reason || 'Desconhecido';
}

function weightedAverage(pairs) {
  // pairs: [{ value, weight }] — ignora entradas sem valor (hotel sem dados suficientes)
  const usable = pairs.filter((p) => p.value !== null && p.value !== undefined && p.weight > 0);
  const totalWeight = usable.reduce((acc, p) => acc + p.weight, 0);
  if (!totalWeight) return null;
  const sum = usable.reduce((acc, p) => acc + p.value * p.weight, 0);
  return Math.round((sum / totalWeight) * 10) / 10;
}

async function fetchHotelSessionsSample(axiosAuth, hotelId, since, until) {
  const res = await axiosAuth
    .get('/human-handoff/admin/sessions', {
      params: { hotel_id: hotelId, since, until, limit: 500, offset: 0 },
    })
    .catch(() => ({ data: [] }));
  return Array.isArray(res.data) ? res.data : [];
}

function computeCloseReasons(sessions) {
  const counts = new Map();
  sessions
    .filter((s) => s.close_reason)
    .forEach((s) => {
      counts.set(s.close_reason, (counts.get(s.close_reason) || 0) + 1);
    });
  return Array.from(counts.entries())
    .map(([reason, count]) => ({ reason, label: reasonLabel(reason), count }))
    .sort((a, b) => b.count - a.count);
}

function computeHourlyPeak(sessions) {
  const byHour = Array(24).fill(0);
  sessions
    .filter((s) => s.requested_at)
    .forEach((s) => {
      byHour[new Date(s.requested_at).getHours()] += 1;
    });
  const max = Math.max(...byHour);
  if (max === 0) return { byHour, peakHour: null };
  const peakHour = byHour.indexOf(max);
  return { byHour, peakHour };
}

// mesma convenção de dashboardMetrics.js::dateKey — componentes LOCAIS, nunca
// `toISOString()` (converte para UTC primeiro; em Portugal, UTC+1 no horário de verão,
// desalinhava sempre o dia). Necessário para o gráfico de evolução da resolução automática
// (DashboardView.jsx) poder juntar esta série por data com `conversationsByDay`/
// `resolutionByDay` sem desalinhar os dias.
function dailyCountsByDate(sessions) {
  const counts = new Map();
  sessions
    .filter((s) => s.requested_at)
    .forEach((s) => {
      const d = new Date(s.requested_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  return Array.from(counts.entries()).map(([date, count]) => ({ date, count }));
}

// Contactos deixados pelo hóspede quando não havia operador disponível (widget:
// HandoffContactForm.jsx) — gravados em `visitor_contact` mas sem nenhum ecrã próprio no
// backoffice; aparecem aqui para a equipa não perder o pedido de retorno de chamada.
function computePendingContacts(sessions) {
  return sessions
    .filter((s) => s.close_reason === 'unavailable' && s.visitor_contact)
    .map((s) => {
      let contact = null;
      try {
        contact = JSON.parse(s.visitor_contact);
      } catch {
        return null;
      }
      if (!contact?.name) return null;
      return {
        session_id: s.id,
        name: contact.name,
        phone: contact.phone || null,
        email: contact.email || null,
        requested_at: s.requested_at,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.requested_at < b.requested_at ? 1 : -1));
}

export async function loadHandoffSummary(axiosAuth, groupId, since, until, hotelId) {
  const { data: allHotels } = await axiosAuth
    .get(`/hotels/group/${groupId}`)
    .catch(() => ({ data: [] }));
  const hotels = hotelId
    ? (allHotels || []).filter((h) => String(h.id) === String(hotelId))
    : allHotels;

  const params = {};
  if (since) params.since = since;
  if (until) params.until = until;

  const results = await Promise.allSettled(
    (hotels || []).map((h) =>
      Promise.all([
        axiosAuth.get('/human-handoff/admin/analytics', { params: { ...params, hotel_id: h.id } }),
        fetchHotelSessionsSample(axiosAuth, h.id, since, until),
      ]).then(([analyticsRes, sessions]) => ({
        hotel: h,
        analytics: analyticsRes.data,
        sessions,
      })),
    ),
  );

  const perHotel = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
  if (perHotel.length === 0) {
    return { available: false };
  }

  const total = perHotel.reduce((acc, { analytics }) => acc + analytics.total, 0);

  const byState = {};
  perHotel.forEach(({ analytics }) => {
    Object.entries(analytics.by_state || {}).forEach(([state, count]) => {
      byState[state] = (byState[state] || 0) + count;
    });
  });

  const ratedCount = perHotel.reduce((acc, { analytics }) => acc + analytics.rated_count, 0);
  const avgRating = weightedAverage(
    perHotel.map(({ analytics }) => ({ value: analytics.avg_rating, weight: analytics.rated_count })),
  );
  const avgWaitSeconds = weightedAverage(
    perHotel.map(({ analytics }) => ({ value: analytics.avg_wait_seconds, weight: analytics.total })),
  );
  const avgHandleSeconds = weightedAverage(
    perHotel.map(({ analytics }) => ({ value: analytics.avg_handle_seconds, weight: analytics.total })),
  );

  const allSessions = perHotel.flatMap(({ sessions }) => sessions);
  const closeReasons = computeCloseReasons(allSessions);
  const { peakHour } = computeHourlyPeak(allSessions);
  const daily = dailyCountsByDate(allSessions);
  const pendingContacts = computePendingContacts(allSessions);

  const hotelRanking = perHotel
    .map(({ hotel, analytics }) => ({ hotel_name: hotel.name, total: analytics.total }))
    .filter((r) => r.total > 0)
    .sort((a, b) => b.total - a.total);

  return {
    available: true,
    total,
    by_state: byState,
    rated_count: ratedCount,
    avg_rating: avgRating,
    avg_wait_seconds: avgWaitSeconds,
    avg_handle_seconds: avgHandleSeconds,
    close_reasons: closeReasons,
    peak_hour: peakHour,
    daily,
    pending_contacts: pendingContacts,
    hotel_ranking: hotelRanking,
  };
}
